import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/seo/SEOHead';
import StructuredData from '../../../components/seo/StructuredData';
import EditableText from '../../../components/cms/EditableText';
import { useCMS } from '../../../contexts/CMSContext';
import { getEffectiveField } from '../../../utils/cmsMobile';

interface ContentSection {
  title: string;
  paragraphs: string[];
}

interface FaqItem {
  question: string;
  answer: string;
}

interface SeoLandingLayoutProps {
  title: string;
  description: string;
  canonicalPath: string;
  heading: string;
  intro: string;
  bullets: string[];
  schema: Record<string, unknown> | Array<Record<string, unknown>>;
  sections?: ContentSection[];
  faqItems?: FaqItem[];
}

export default function SeoLandingLayout({
  title,
  description,
  canonicalPath,
  heading,
  intro,
  bullets,
  schema,
  sections = [],
  faqItems = [],
}: SeoLandingLayoutProps) {
  const { data, isEditMode, isMobileContentActive } = useCMS();
  const resolvedFaqItems = faqItems.map((item, index) => {
    const id = index + 1;
    return {
      id,
      question:
        getEffectiveField(data, 'seo', `seoFaqQ${id}`, isMobileContentActive) || item.question,
      answer:
        getEffectiveField(data, 'seo', `seoFaqA${id}`, isMobileContentActive) || item.answer,
    };
  });

  const faqSchema =
    resolvedFaqItems.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: resolvedFaqItems.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }
      : null;

  const schemaData = faqSchema
    ? Array.isArray(schema)
      ? [...schema, faqSchema]
      : [schema, faqSchema]
    : schema;

  return (
    <div className="font-sans bg-[#FAF8F5] min-h-screen">
      <SEOHead
        title={title}
        description={description}
        canonicalPath={canonicalPath}
        keywords="Dario Rojas, nutricionista Palma de Mallorca, dietista Palma, adelgazar, nutricion deportiva, buena alimentacion"
      />
      <StructuredData data={schemaData} />
      <Navbar />

      <main className="pt-28 pb-24">
        <section className="max-w-5xl mx-auto px-6">
          <h1 className="font-serif text-4xl md:text-6xl font-semibold text-[#1A1A1A] leading-tight">
            {heading}
          </h1>
          <p className="text-[#6B6B6B] text-lg leading-relaxed mt-6 max-w-3xl">{intro}</p>

          <div className="bg-white border border-stone-100 rounded-2xl p-6 md:p-8 mt-10">
            <h2 className="font-semibold text-2xl text-[#1A1A1A] mb-4">Como te ayudo</h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {bullets.map((bullet) => (
                <li key={bullet} className="text-[#1A1A1A] text-sm flex items-start gap-2">
                  <span className="mt-1 w-2 h-2 rounded-full bg-[#8B9D83] shrink-0" />
                  {bullet}
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-3 mt-8">
              <a
                href="/#agendar"
                className="bg-[#1A1A1A] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#333] transition-colors"
              >
                Reservar cita
              </a>
              <Link
                to="/"
                className="border border-[#1A1A1A] text-[#1A1A1A] px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#1A1A1A] hover:text-white transition-colors"
              >
                Volver al inicio
              </Link>
            </div>
          </div>

          {sections.map((section) => (
            <section key={section.title} className="mt-12">
              <h2 className="font-semibold text-2xl text-[#1A1A1A] mb-4">{section.title}</h2>
              <div className="flex flex-col gap-4">
                {section.paragraphs.map((paragraph, index) => (
                  <p key={`${section.title}-${index}`} className="text-[#4A4A4A] leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>
          ))}

          {resolvedFaqItems.length > 0 && (
            <section className="mt-14">
              <EditableText
                section="seo"
                field="seoFaqTitle"
                tag="h2"
                className="font-semibold text-2xl text-[#1A1A1A] mb-4"
              />
              <div className="flex flex-col gap-3">
                {resolvedFaqItems.map((item) => (
                  <details
                    key={item.id}
                    className="border border-stone-200 rounded-xl p-4 bg-white"
                  >
                    <summary className="cursor-pointer text-[#1A1A1A] font-medium">
                      <EditableText
                        section="seo"
                        field={`seoFaqQ${item.id}`}
                        tag="span"
                        className="text-[#1A1A1A] font-medium"
                      />
                    </summary>
                    <EditableText
                      section="seo"
                      field={`seoFaqA${item.id}`}
                      tag="p"
                      multiline
                      className="text-[#4A4A4A] text-sm mt-3 leading-relaxed"
                    />
                  </details>
                ))}
              </div>
            </section>
          )}

          <nav aria-label="Enlaces internos SEO" className="mt-10">
            <EditableText
              section="seo"
              field="seoMoreInfoTitle"
              tag="p"
              className="text-xs font-bold uppercase tracking-widest text-[#8B9D83] mb-3"
            />
            <div className="flex flex-wrap gap-3">
              {isEditMode ? (
                <>
                  <EditableText section="seo" field="seoLink1" tag="span" className="text-sm text-[#1A1A1A]" />
                  <EditableText section="seo" field="seoLink2" tag="span" className="text-sm text-[#1A1A1A]" />
                  <EditableText section="seo" field="seoLink3" tag="span" className="text-sm text-[#1A1A1A]" />
                  <EditableText section="seo" field="seoLink4" tag="span" className="text-sm text-[#1A1A1A]" />
                  <EditableText section="seo" field="seoLink5" tag="span" className="text-sm text-[#1A1A1A]" />
                  <EditableText section="seo" field="seoLink6" tag="span" className="text-sm text-[#1A1A1A]" />
                </>
              ) : (
                <>
                  <Link to="/nutricionista-palma" className="text-sm text-[#1A1A1A] underline underline-offset-4">
                    {getEffectiveField(data, 'seo', 'seoLink1', isMobileContentActive)}
                  </Link>
                  <Link to="/nutricionista-palma-de-mallorca" className="text-sm text-[#1A1A1A] underline underline-offset-4">
                    {getEffectiveField(data, 'seo', 'seoLink2', isMobileContentActive)}
                  </Link>
                  <Link to="/dietista-palma-de-mallorca" className="text-sm text-[#1A1A1A] underline underline-offset-4">
                    {getEffectiveField(data, 'seo', 'seoLink3', isMobileContentActive)}
                  </Link>
                  <Link to="/nutricionista-clinico-palma-de-mallorca" className="text-sm text-[#1A1A1A] underline underline-offset-4">
                    {getEffectiveField(data, 'seo', 'seoLink4', isMobileContentActive)}
                  </Link>
                  <Link to="/adelgazar-palma-de-mallorca" className="text-sm text-[#1A1A1A] underline underline-offset-4">
                    {getEffectiveField(data, 'seo', 'seoLink5', isMobileContentActive)}
                  </Link>
                  <Link to="/nutricion-deportiva-palma-de-mallorca" className="text-sm text-[#1A1A1A] underline underline-offset-4">
                    {getEffectiveField(data, 'seo', 'seoLink6', isMobileContentActive)}
                  </Link>
                </>
              )}
            </div>
          </nav>
        </section>
      </main>

      <Footer />
    </div>
  );
}
